import * as GC from '@grapecity/spread-sheets';
import { IExportData, IExportItem } from '@models/export/export-item';
import { colours, FONT_NAME, FONT_SIZE } from '@shared/spread/spread-settings';
import { Toast } from 'angular2-toaster/src/toast';

const MAX_TAB_LEN = 31; // Maximum tab name length in Excel
const TAB_COLOR_FIXED = '#DDEBF7';

export const checkExportItemHasAllCalibrationsLoaded = (exportItem: IExportItem) =>
  !!(exportItem.Beta && exportItem.Decay && exportItem.StableBalance);

export const getEmptyExportData = (): IExportData => ({
  Settings: null,
  MarketRates: null,
  Acquisitions: null,
  ConcentrationReport: null,
  CurrentPeriodSummary: null,
  ExportItems: null,
  ActiveProductGroups: null
});

export const getEmptyExportItem = (): IExportItem => ({
  Product: null,
  Beta: {
    ProductCalibrations: [],
    ProductTimeSeries: null
  },
  Decay: {
    ProductCalibrations: [],
    DecayResult: null,
    ApprovedDecayFactors: [],
    ProposedDecayFactors: []
  },
  StableBalance: {
    ProductCalibrations: [],
    AcquisitionCoefficients: []
  },
  VintageRunOffResponse: null,
  Wal: [
    {
      RunOffType: null,
      Wal: null
    }
  ],

  // Fields used in the Export Config View (ECV)
  HasProposeRecords: false,
  HasActiveRecords: false,
  IsChecked: false,
  IsFullyLoaded: false
});

export const EXPORT_TOASTS = {
  getExportDataError: {
    title: 'Failed To Get Export Data',
    body: '',
    type: 'error',
    showCloseButton: true
  } as Toast,
  getHighchartsImageError: {
    title: 'Failed To Get Chart Data',
    body: '',
    type: 'error',
    showCloseButton: true
  } as Toast,
  creatingReportError: {
    title: 'Error Creating Export',
    type: 'error',
    showCloseButton: true,
    body: ''
  }
};

export enum EXPORT_CONTENT_TYPES {
  Beta = 1,
  Decay = 2,
  StableBalance = 3,
  ProductDetail = 4
}

export const exportDialogSizes = {
  exportConfig: {
    width: '95%',
    height: '83%',
    productList: {
      height: `18.25rem`
    }
  },
  exportPreview: {
    width: '95%',
    height: '83%',
    spreadsheet: {
      height: `calc(100% - 50px)` // Of the
    }
  }
};

export const EXPORT_FIXED_TABS = [
  {
    displayName: 'Calibration Settings',
    id: 'calibration-settings',
    IsChecked: false, // Unchecked by default
    tabColor: TAB_COLOR_FIXED,
    IsFullyLoaded: false
  },
  {
    displayName: 'Product Summary',
    id: 'product-summary',
    IsChecked: false, // Unchecked by default
    tabColor: TAB_COLOR_FIXED,
    IsFullyLoaded: false
  },
  {
    displayName: 'Concentration Report',
    id: 'concentration-report',
    IsChecked: false, // Unchecked by default
    tabColor: TAB_COLOR_FIXED,
    IsFullyLoaded: false
  }
];

export interface IExportFixedTab {
  displayName: string;
  id: string;
  IsChecked: boolean;
  IsFullyLoaded: boolean;
  tabColor?: string;
}

export type IExportFilterType = 'export-filter-propose' | 'export-filter-active';
export const EXPORT_TYPES = {
  propose: { id: 'export-filter-propose' as IExportFilterType, label: `Use Proposed Calibrations` },
  active: { id: 'export-filter-active' as IExportFilterType, label: `Active Calibrations Only` }
};

export const EXPORT_TAB_NAMES = {
  fixedTabs: {
    calibrationSettings: `Calibration Settings`,
    productSummary: `Product Summary`,
    concentrationReport: `Concentration Report`
  }
};

export const prepareSheetNameForExport = (sheetName: string = null, suffix: string = null): string => {
  if (sheetName !== null && sheetName.length > 0 && suffix !== null && suffix.length > 0) {
    sheetName = [`${sheetName}`, `${suffix}`].join('-');
  }

  // Limit to the max. char length in excel
  if (sheetName) {
    const finalTabName = sheetName.length > MAX_TAB_LEN ? `${sheetName.slice(0, MAX_TAB_LEN - 3)}...` : sheetName;
    return finalTabName;
  } else {
    console.error('sheetName missing in prepareSheetNameForExport');
  }
};

export const getStartColumn = (type: EXPORT_CONTENT_TYPES, exportItem: IExportItem = null): number => {
  const startColumn = {
    stableBalance: 1,
    decay: 21,
    beta: 39,
    productDetail: 55
  };

  const noStableBalanceData = (exportItem.StableBalance.ProductCalibrations || []).length === 0;
  const noDecayData = (exportItem.Decay.ProductCalibrations || []).length === 0;
  const noBetaData = (exportItem.Beta.ProductCalibrations || []).length === 0;
  let prevContentNoData = 0;

  switch (type) {
    // StableBalance (1st)
    case EXPORT_CONTENT_TYPES.StableBalance:
      return startColumn.stableBalance;
    // Decay (2nd)
    case EXPORT_CONTENT_TYPES.Decay:
      if (noStableBalanceData) {
        return startColumn.stableBalance;
      } // If there is none other previous calib. data
      return startColumn.decay;
    // Beta
    case EXPORT_CONTENT_TYPES.Beta:
      prevContentNoData = [noStableBalanceData, noDecayData].filter(Boolean).length;
      if (prevContentNoData === 1) {
        return startColumn.decay;
      }
      if (prevContentNoData === 2) {
        return startColumn.stableBalance;
      }
      return startColumn.beta;
    // ProductDetail
    case EXPORT_CONTENT_TYPES.ProductDetail:
      // How Much Calibrations have no data?
      prevContentNoData = [noStableBalanceData, noDecayData, noBetaData].filter(Boolean).length;
      // Position based on that
      if (prevContentNoData === 1) {
        return startColumn.beta;
      }
      if (prevContentNoData === 2) {
        return startColumn.decay;
      }
      // if (prevContentNoData === 3) return startColumn.stableBalance;
      return startColumn.productDetail;
    default:
      return 0;
  }
};

// Dynamic calculation of chart size and position
export const CELL_WIDTH = 100;
export const CELL_HEIGHT = 20;

export interface IExportChartOption {
  rowSpaceUsed: number;
  colSpaceUsed: number;
  posX: number;
  posY?: number;
  width: number;
  height: number;
  id: string;
}

export interface IExportChartsOptions {
  stableBalance: IExportChartOption;
  decay: IExportChartOption;
  beta: IExportChartOption;
  betaScatterPlots: IExportChartOption;
  productSummaryReport: IExportChartOption;
}

export const getExportChartOptions = (exportItem: IExportItem): IExportChartsOptions => {
  let rowSpaceUsed;
  let colSpaceUsed;
  let colOffset;

  // === STABLE BALANCE ===
  colOffset = getStartColumn(EXPORT_CONTENT_TYPES.StableBalance, exportItem);
  colSpaceUsed = 18;
  rowSpaceUsed = 30;
  const stableBalance = {
    rowSpaceUsed,
    colSpaceUsed,
    posX: CELL_WIDTH * colOffset,
    width: Math.round(CELL_WIDTH * colSpaceUsed),
    height: Math.round(CELL_HEIGHT * rowSpaceUsed),
    id: 'Stable-Balance-Chart-'
  };

  // === DECAY ===
  colOffset = getStartColumn(EXPORT_CONTENT_TYPES.Decay, exportItem);
  colSpaceUsed = 14;
  rowSpaceUsed = 30;
  const decay = {
    rowSpaceUsed,
    colSpaceUsed,
    posX: CELL_WIDTH * colOffset,
    width: Math.round(CELL_WIDTH * colSpaceUsed),
    height: Math.round(CELL_HEIGHT * rowSpaceUsed),
    id: 'Decay-Chart-'
  };

  // === BETA ===
  colOffset = getStartColumn(EXPORT_CONTENT_TYPES.Beta, exportItem);
  rowSpaceUsed = 15;

  colSpaceUsed = 8;
  const marginPx = 20;
  const beta = {
    rowSpaceUsed,
    colSpaceUsed,
    posX: CELL_WIDTH * colOffset,
    width: Math.round(CELL_WIDTH * colSpaceUsed),
    height: Math.round(CELL_HEIGHT * rowSpaceUsed),
    id: 'Beta-Chart-'
  };
  colSpaceUsed = 7;
  const betaScatterPlots = {
    rowSpaceUsed,
    colSpaceUsed,
    posX: marginPx + CELL_WIDTH * (colOffset + beta.colSpaceUsed),
    width: Math.round(CELL_WIDTH * colSpaceUsed) - marginPx,
    height: Math.round(CELL_HEIGHT * rowSpaceUsed),
    id: 'Beta-Scatter-Chart-'
  };

  // === PRODUCT SUMMARY REPORT ===
  const productSummaryReport = {
    rowSpaceUsed: 0,
    colSpaceUsed: 0,
    posX: 0,
    width: Math.round(500),
    height: Math.round((500 / 100) * 6 * 10),
    id: 'Product-Summary-Chart-'
  };

  // const productDetailReport = {
  //   id: 'Product-Detail-Report-'
  // };

  return { decay, stableBalance, beta, betaScatterPlots, productSummaryReport };
};

export const exportChangeLogColours = {
  levelTwo: {
    text: 'black',
    summary: colours.DARK_BLUE,
    noComment: '#717171'
  }
};

export const NO_COMMENT_DISPLAY_TEXT = 'Change has no comment';

export const setChangeLogComment = (cell: GC.Spread.Sheets.CellRange, comment: string = null) => {
  if (comment) {
    // Comment
    cell
      .text(`${comment}`)
      .foreColor(exportChangeLogColours.levelTwo.text)
      .font(`${FONT_SIZE}pt ${FONT_NAME}`);
  } else {
    // No Comment:
    cell
      .text(NO_COMMENT_DISPLAY_TEXT)
      .foreColor(exportChangeLogColours.levelTwo.noComment)
      .font(`italic ${FONT_SIZE}pt ${FONT_NAME}`);
  }
};
